import React, { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Crown, Check } from "lucide-react";
import { toast } from "sonner";

const PLANS = [
  {
    lookup_key: "bloom_monthly", name: "Monthly", price: "$4.99", cadence: "per month",
    highlights: ["Unlimited AI reflections", "All Premium challenges", "Downloadable journals", "Ad-free forever"],
    accent: "#E07A5F",
  },
  {
    lookup_key: "bloom_yearly", name: "Yearly", price: "$49.90", cadence: "per year (save ~17%)",
    highlights: ["Everything monthly", "2 months free", "Priority feature requests"],
    accent: "#81B29A", popular: true,
  },
  {
    lookup_key: "creator_monthly", name: "Creator Pass", price: "$9.99", cadence: "per month",
    highlights: ["Everything in Premium", "Creator content templates", "Growth playbook + hooks", "Community spotlight"],
    accent: "#F2CC8F",
  },
];

export default function Premium() {
  const { user, refresh } = useAuth();
  const [loadingKey, setLoadingKey] = useState(null);

  const subscribe = async (lookup_key) => {
    setLoadingKey(lookup_key);
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout");
      setLoadingKey(null);
    }
  };

  return (
    <div className="space-y-10" data-testid="premium-page">
      <div className="text-center">
        <span className="tag-chip bg-[#2D3142] text-[#F2CC8F]"><Crown className="w-3 h-3" /> Bloom Premium</span>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight mt-4">Support your practice.</h1>
        <p className="text-[#5b5f6e] mt-3 max-w-xl mx-auto">
          Premium unlocks unlimited AI reflections, exclusive challenges, and creator resources —
          all for the price of a single coffee.
        </p>
        {user?.is_premium && (
          <p className="mt-4 inline-block tag-chip bg-[#81B29A] text-white">You're already Premium — thank you 🌱</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {PLANS.map((p) => (
          <div key={p.lookup_key} className={`soft-card p-8 relative ${p.popular ? "ring-2 ring-[#81B29A]" : ""}`} data-testid={`plan-${p.lookup_key}`}>
            {p.popular && <span className="absolute -top-3 left-8 tag-chip bg-[#81B29A] text-white">Most loved</span>}
            <p className="text-sm uppercase tracking-widest text-[#8A8D98]">{p.name}</p>
            <p className="font-display text-5xl mt-2" style={{ color: p.accent }}>{p.price}</p>
            <p className="text-sm text-[#8A8D98]">{p.cadence}</p>
            <ul className="mt-6 space-y-3">
              {p.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-2 text-[#2D3142]">
                  <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: p.accent }} /> {h}
                </li>
              ))}
            </ul>
            <button onClick={()=>subscribe(p.lookup_key)} disabled={loadingKey===p.lookup_key || user?.is_premium}
              className="mt-8 w-full pill-btn text-white disabled:opacity-50"
              style={{ background: p.accent }}
              data-testid={`subscribe-${p.lookup_key}`}
            >
              {user?.is_premium ? "You're subscribed" : (loadingKey===p.lookup_key ? "Opening checkout…" : "Subscribe")}
            </button>
          </div>
        ))}
      </div>

      <p className="text-xs text-[#8A8D98] text-center max-w-xl mx-auto">
        Payments are securely handled by Stripe. You can cancel anytime from your customer portal.
        Test card: 4242 4242 4242 4242 · any future expiry · any CVC.
      </p>
    </div>
  );
}
